import { useStorageUrl } from '../components/chatHome/MemberAvatar';
import { pathFromPublicUrl } from '../services/storageService';

/**
 * Resolve an agent picture (avatar or gallery shot) to something a bare <img> can actually load.
 *
 * Four shapes arrive: a same-origin asset path (starts with "/"), a raw Storage path, one of OUR tokenless public URLs
 * (which 403 on the auth-only gallery rules, because an <img> sends no credentials), or a genuinely external
 * or already-tokened URL. The first two resolve through the web SDK (authenticated -> token URL); the last
 * passes through untouched. Both the audition frame and the candidate bench need this, so it lives once here.
 */
export function useAgentImage(source?: string | null): string | null {
  /*
   * A LEADING SLASH MEANS THIS ORIGIN, NOT THE BUCKET (2026-09-25). Storage object paths are relative —
   * `agent-avatars/x.jpg`, `characters/<uid>/side.jpg` — and never start with "/". So a source that does is a
   * same-origin web asset and must be handed to the <img> untouched; looking it up in Storage finds nothing
   * and renders the empty state instead. This is what lets a local emulator run serve the avatars straight
   * from `public/` (see `npm run seed:avatars`) with no bucket read, no auth and no cross-environment access.
   */
  const isSameOriginAsset = !!source && source.startsWith('/');
  const objectPath = source && !isSameOriginAsset
    ? (!source.startsWith('http') && !source.startsWith('data:') ? source : pathFromPublicUrl(source))
    : null;
  const resolved = useStorageUrl(objectPath);
  return objectPath ? resolved : (source || null);
}

export default useAgentImage;
